<script setup lang="ts">
    import * as yup from 'yup'
    const phoneRegExp = /^([1-9]{1}[0-9]{9})$/;
    const passwordRegExp = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,32}$/;

    const form = ref(null);
    const isSuccess = ref(false);

    const schema = yup.object().shape({
        firstname: yup.string().required('First Name is required'),
        lastname: yup.string().required('Last Name is required'),
        email: yup.string().email('Invalid email format').required('Email is required.'),
        contact: yup.string().matches(phoneRegExp, "Invalid phone format").required('Phone is required').max(10),
        password: yup.string().matches(passwordRegExp, "Please enter a combination of alphabetic and numeric characters between 8 and 32").required('Password is required'),
        password_confirmation: yup.string()
            .oneOf([yup.ref('password')], 'Passwords must match')
            .required('Confirm Password is required'),
    })

    const formData = ref({
        firstname: null,
        lastname: null,
        email: null,
        contact: null,
        password: null,
        password_confirmation: null,
    });

    const { handleSubmit, resetForm, setFieldError } = useForm({
        validationSchema: schema,
        initialValues: formData
    });

    const onInvalidSubmit = ({ errors }: any) => {
        console.log('invalid', errors)
        const firstErrorFieldName = Object.keys(errors)[0];
        // scrollToValidation(firstErrorFieldName);
    }

    const onSuccess = async(values: any) => {

        console.log('valid', values);
        // resetForm();
        formData.value = values

        try {
            const { data, status }: any = await useApi(`/api/user/register`, {
                method: 'POST',
                body: formData.value
            })

            if(data && status == 'success') {
                isSuccess.value = true;
                showNotificationSuccess(
                    'Registration has been submitted.', 
                    'Please report to the administrator if there is anything wrong.',
                );
                handleSuccess();
            }

        } catch(error: any) {
            if (error.response) {
                const { statusCode, errorMessage, serverErrorMessage, serverErrorObject } = error.response._data?.error || {};

                console.error('Error', {
                    statusCode: statusCode,
                    errorMessage: errorMessage,
                    serverErrorMessage: serverErrorMessage,
                    serverErrorObject: serverErrorObject
                })

                if(serverErrorMessage == 'The email has already been taken.') {
                    setFieldError('email', 'The email has already been taken.')
                }
                else {
                    showNotificationError(serverErrorMessage);
                }


            } else {
                console.error("Unexpected error:", error);
            }
        }
    }

    const submit = handleSubmit(onSuccess, onInvalidSubmit)

    const scrollToValidation = (errorName: any) => {
        const el = document.querySelector(`[name="${errorName}"]`);
        if (el) {
            el.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    };

    // Exposes the submit button to the parent.
    // For parent access
    defineExpose({
        submit
    });

    // For parent processing
    const emit = defineEmits<{
        (e: 'closeEvent'): void,
        (e: 'successEvent'): void
    }>()

    const handleExit = () => {
        emit('closeEvent') // Emit the event when the button is clicked
    }

    const handleSuccess = () => {
        emit('successEvent') // Emit the event when the button is clicked
    }
</script>

<template>
    <Block>
        <Block 
            v-if="!isSuccess"
            class="m-auto w-full px-[3.6rem] py-[2rem] bg-white mx-auto rounded-[1rem] max-md:p-[1.6rem]">
            <form
                ref="form"
                @submit="submit">
                <TitleText
                    class="text-center"
                    type="4">
                    Free Member Registration
                </TitleText>
                <p class="text-[1.4rem] leading-[1.5] mt-[2.4rem] max-md:mt-[1.2rem]">Fields marked with <span class="text-[redEmphasis]">*</span> are required.</p>
                <Block class="mt-[2.4rem]">
                    <Block class="flex items-center flex-1 gap-[.8rem] max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem] max-md:mb-0" for="firstname">Full Name <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex w-full items-start">
                            <Block class="flex flex-wrap w-full">
                                <InputText
                                    class=" font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                    inputName="firstname"
                                    icon="none"
                                    inputType="text"
                                    placeholder="First Name"
                                />
                            </Block>
                            <Block class="flex flex-wrap w-full ml-[1rem] ">
                                <InputText
                                    class=" font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                    inputName="lastname"
                                    icon="none"
                                    inputType="text"
                                    placeholder="Last Name"
                                />
                            </Block>
                        </Block>
                    </Block>

                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem] max-md:mb-0" for="email">Email Address <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputText
                                class="placeholder-[secondary] font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                inputName="email"
                                icon="none"
                                inputType="email"
                                placeholder="mail@kichieats.ph"
                            />
                        </Block>
                    </Block>

                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem] max-md:mb-0" for="phone">Phone Number <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputPhone
                                class="border-lightGray border-solid border-[1px] rounded-[1rem] overflow-hidden"
                                inputName="contact"
                                placeholder="00000000000"
                            />
                        </Block>
                    </Block>

                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem] max-md:mb-0" for="password">Password <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputPassword
                                class="font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                inputName="password"
                                icon="none"
                                placeholder=""
                            />
                        </Block>
                    </Block>

                    <Block class="mt-[1.6rem] flex flex-1 gap-[.8rem] items-center max-md:flex-col max-md:items-start">
                        <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block min-w-[19.2rem] max-md:mb-0" for="password_confirmation">Confirm Password <span class="text-[redEmphasis]">*</span></label>
                        <Block class="flex flex-wrap w-full">
                            <InputPassword
                                class="font-normal text-[1.4rem] py-[.8rem] px-[1.6rem] border-lightGray border-solid border-[1px] !rounded-[1rem]"
                                inputName="password_confirmation"
                                icon="none"
                                placeholder=""
                            />
                        </Block>
                    </Block>
                </Block>
            </form>
        </Block>
        <Block 
            v-if="isSuccess"
            class="m-auto w-full px-[3.6rem] py-[2rem] bg-white mx-auto rounded-[1rem]">
            <TitleText
                class="text-center"
                type="4">
                Thank you!
            </TitleText>
            <p class="text-center text-[1.6rem] leading-[1.5] mt-[3.2rem]">A confirmation email was sent to the email address you entered. <br>
                After accessing the URL in the e-mail, your registration will be completed.</p>
            <Block class="bg-[bgSecond] p-[1.6rem] mt-[3.2rem]">
                <h4 class="text-[1.6rem] leading-[1.5] text-center">Email Address</h4>
                <p class="text-[1.6rem] text-center font-bold leading-[1.5]" v-if="formData.email">{{ formData.email }}</p>
            </Block>
            <Block class="mt-[3.2rem]">
                <!-- TODO NO EMAIL BEFORE -->
                <h2 class="text-[1.8rem] pl-[2.6rem] relative leading-[1.5] font-bold">If you do not receive an email</h2>
                <ul class="mt-[.6rem] list-disc pl-[2rem]">
                    <li class="text-[1.4rem] leading-[1.5]">Please check the email address you entered.</li>
                    <li class="text-[1.4rem] leading-[1.5]">Please check your spam folder to make sure it has not been sorted into your junk mail folder.</li>
                </ul>
            </Block>

            <button 
                type="button"
                @click="handleExit"
                class="mt-[2.4rem] mx-auto block text-[1.4rem] leading-[1.5] font-bold text-[primary] border-[.1rem] border-[darkGray] rounded-[1rem] max-w-[25.2rem] w-full p-[.8rem]">
                Close
            </button>
        </Block>
    </Block>
</template>

